// Image thumbnails of attachment chips (decision D36.4; rendered by AttachmentChips.tsx), in the composer (from the
// local File being uploaded) and in the conversation history (from an authenticated GET of the stored bytes). Always an
// <img> with a blob: URL re-typed with the stored image type: an image, SVG included, never runs script or loads
// anything, and the markup of an SVG is never inserted into the page. A format the browser cannot decode (HEIC, TIFF in
// most browsers) falls back to the icon.
import { useEffect, useState } from "react";
import type { AssistantAttachmentKind } from "@maestros/contracts";
import { fetchAssistantAttachmentContent } from "../../api/endpoints-composer.ts";

export type ContentFetcher = (id: string, signal?: AbortSignal) => Promise<Blob>;

/** Stored bytes already fetched in this tab (a history re-render or a reopened panel never refetches). */
const cache = new Map<string, Promise<Blob>>();
const CACHE_MAX = 60;

function cachedContent(id: string, fetcher: ContentFetcher): Promise<Blob> {
  const hit = cache.get(id);
  if (hit) return hit;
  const pending = fetcher(id).catch((e: unknown) => {
    cache.delete(id);
    throw e;
  });
  cache.set(id, pending);
  if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value as string);
  return pending;
}

/** Test seam: forget the fetched thumbnails. */
export function clearThumbnailCache(): void {
  cache.clear();
}

export interface PreviewSource {
  kind: AssistantAttachmentKind;
  mime?: string | undefined;
  /** The local file (composer, before or while uploading). */
  file?: Blob | undefined;
  /** The stored attachment (history, or the composer once uploaded without a local file). */
  attachmentId?: string | undefined;
}

const canObjectUrl = () => typeof URL !== "undefined" && typeof URL.createObjectURL === "function";

/** A blob: URL of the image, or null (not an image, not loaded yet, failed, or no object URLs in this environment). */
export function useAttachmentPreview(src: PreviewSource, fetcher: ContentFetcher = fetchAssistantAttachmentContent): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const { kind, mime, file, attachmentId } = src;
  useEffect(() => {
    if (kind !== "image" || !mime || !mime.startsWith("image/") || !canObjectUrl()) return undefined;
    let created: string | null = null;
    let cancelled = false;
    const show = (blob: Blob) => {
      if (cancelled) return;
      // Re-typed with the image type: the server answers an SVG as application/octet-stream.
      created = URL.createObjectURL(blob.type === mime ? blob : new Blob([blob], { type: mime }));
      setUrl(created);
    };
    if (file) show(file);
    else if (attachmentId) void cachedContent(attachmentId, fetcher).then(show, () => undefined);
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
      setUrl(null);
    };
  }, [kind, mime, file, attachmentId, fetcher]);
  return url;
}

